
import { supabase } from "@/integrations/supabase/client";
import { Currency } from "@/lib/types";
import { useSystemSettings } from "@/hooks/useSystemSettings";

export interface ExchangeRate {
  id: string;
  baseCurrencyId: string;
  targetCurrencyId: string;
  rate: number;
  updatedAt: Date;
}

export type ExchangeProvider = 
  | "exchange-api" 
  | "openexchangerates" 
  | "exchangerate-api" 
  | "exchangeratesapi" 
  | "freecurrencyapi";

export const EXCHANGE_PROVIDERS = {
  "exchange-api": {
    name: "Exchange API (Free)",
    url: "https://github.com/fawazahmed0/exchange-api",
    apiKeyRequired: false,
    apiKeyUrl: "",
    description: "Free and open source exchange rates API"
  },
  "openexchangerates": {
    name: "Open Exchange Rates",
    url: "https://openexchangerates.org/",
    apiKeyRequired: true,
    apiKeyUrl: "https://openexchangerates.org/signup",
    description: "Real-time exchange rates API"
  },
  "exchangerate-api": {
    name: "Exchange Rate API",
    url: "https://www.exchangerate-api.com/",
    apiKeyRequired: true,
    apiKeyUrl: "https://www.exchangerate-api.com/docs/free",
    description: "Free and paid exchange rate API"
  },
  "exchangeratesapi": {
    name: "Exchange Rates API",
    url: "https://exchangeratesapi.io/",
    apiKeyRequired: true,
    apiKeyUrl: "https://exchangeratesapi.io/pricing/",
    description: "Foreign exchange rates API with currency conversion"
  },
  "freecurrencyapi": {
    name: "Free Currency API",
    url: "https://freecurrencyapi.com/",
    apiKeyRequired: true,
    apiKeyUrl: "https://freecurrencyapi.com/register",
    description: "Free currency conversion API"
  }
};

export class CurrencyConverter {
  private static instance: CurrencyConverter;
  private exchangeRatesCache: Map<string, ExchangeRate> = new Map();
  private currentProvider: ExchangeProvider = "exchange-api";
  private apiKeys: Record<string, string | null> = {
    "exchange-api": null, // No API key needed
    "openexchangerates": null,
    "exchangerate-api": null,
    "exchangeratesapi": null,
    "freecurrencyapi": null
  };
  
  private constructor() {}
  
  public static getInstance(): CurrencyConverter {
    if (!CurrencyConverter.instance) {
      CurrencyConverter.instance = new CurrencyConverter();
    }
    return CurrencyConverter.instance;
  }
  
  public setProvider(provider: ExchangeProvider): void {
    this.currentProvider = provider;
    console.log(`Exchange rate provider set to: ${provider}`);
  }
  
  public async setApiKey(provider: ExchangeProvider, apiKey: string): Promise<void> {
    this.apiKeys[provider] = apiKey;
    console.log(`API key set for provider: ${provider}`);
    
    // Store the API key in user preferences
    try {
      const { data: userResponse, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      
      const userId = userResponse.user?.id;
      if (!userId) return;
      
      const { error } = await supabase
        .from('user_preferences')
        .update({ 
          [`${provider}_api_key`]: apiKey
        })
        .eq('user_id', userId);
      
      if (error) {
        console.error(`Error storing API key for ${provider}:`, error);
      }
    } catch (error) {
      console.error(`Error storing API key for ${provider}:`, error);
    }
  }
  
  public getApiKey(provider: ExchangeProvider): string | null {
    return this.apiKeys[provider];
  }
  
  public getCurrentProvider(): ExchangeProvider {
    return this.currentProvider;
  }
  
  public getProviderInfo(provider: ExchangeProvider) {
    return EXCHANGE_PROVIDERS[provider];
  }
  
  public async loadUserPreferences(): Promise<void> {
    try {
      const { data: userResponse, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      
      const userId = userResponse.user?.id;
      if (!userId) return;
      
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', userId)
        .single();
      
      if (error) {
        console.error("Error loading exchange provider preferences:", error);
        return;
      }
      
      if (data?.exchange_provider) {
        this.setProvider(data.exchange_provider as ExchangeProvider);
      }
      
      // Load API keys
      if (data?.openexchangerates_api_key) {
        this.apiKeys.openexchangerates = data.openexchangerates_api_key;
      }
      
      if (data?.['exchangerate-api_api_key']) {
        this.apiKeys['exchangerate-api'] = data['exchangerate-api_api_key'];
      }
      
      if (data?.exchangeratesapi_api_key) {
        this.apiKeys.exchangeratesapi = data.exchangeratesapi_api_key;
      }
      
      if (data?.freecurrencyapi_api_key) {
        this.apiKeys.freecurrencyapi = data.freecurrencyapi_api_key;
      }
      
    } catch (error) {
      console.error("Error loading exchange provider preferences:", error);
    }
  }
  
  public async convert(
    amount: number, 
    fromCurrencyId: string, 
    toCurrencyId: string
  ): Promise<{ convertedAmount: number; rate: number }> {
    // If currencies are the same, no conversion needed
    if (fromCurrencyId === toCurrencyId) {
      return { convertedAmount: amount, rate: 1 };
    }
    
    try {
      const rate = await this.getExchangeRate(fromCurrencyId, toCurrencyId);
      if (!rate) {
        console.warn(`No exchange rate found for ${fromCurrencyId} to ${toCurrencyId}`);
        return { convertedAmount: amount, rate: 1 }; // Fall back to original amount
      }
      
      return {
        convertedAmount: amount * rate,
        rate
      };
    } catch (error) {
      console.error("Error converting currency:", error);
      return { convertedAmount: amount, rate: 1 }; // Fall back to original amount
    }
  }
  
  private async getExchangeRate(fromCurrencyId: string, toCurrencyId: string): Promise<number | null> {
    const cacheKey = `${fromCurrencyId}-${toCurrencyId}`;
    
    // Check cache first
    const cachedRate = this.exchangeRatesCache.get(cacheKey);
    if (cachedRate && this.isRateFresh(cachedRate.updatedAt)) {
      return cachedRate.rate;
    }
    
    // Try to get from database
    const { data: rateData, error } = await supabase.rpc(
      'get_exchange_rate',
      { from_currency: fromCurrencyId, to_currency: toCurrencyId }
    );
    
    if (error) {
      console.error("Error fetching exchange rate:", error);
      return null;
    }
    
    if (rateData !== null) {
      // Cache the result
      this.exchangeRatesCache.set(cacheKey, {
        id: `${fromCurrencyId}-${toCurrencyId}`,
        baseCurrencyId: fromCurrencyId,
        targetCurrencyId: toCurrencyId,
        rate: rateData,
        updatedAt: new Date()
      });
      
      return rateData;
    }
    
    // If no rate in database, try to fetch from external API
    return await this.fetchExchangeRateFromAPI(fromCurrencyId, toCurrencyId);
  }
  
  private isRateFresh(updatedAt: Date): boolean {
    // Consider rates fresh for 1 hour
    const ONE_HOUR = 60 * 60 * 1000;
    return (new Date().getTime() - updatedAt.getTime()) < ONE_HOUR;
  }
  
  private async fetchExchangeRateFromAPI(fromCurrencyId: string, toCurrencyId: string): Promise<number | null> {
    // Need to get currency codes from IDs
    const { data: fromCurrency, error: fromError } = await supabase
      .from('currencies')
      .select('code')
      .eq('id', fromCurrencyId)
      .single();
      
    if (fromError) {
      console.error("Error fetching base currency:", fromError);
      return null;
    }
    
    const { data: toCurrency, error: toError } = await supabase
      .from('currencies')
      .select('code')
      .eq('id', toCurrencyId)
      .single();
      
    if (toError) {
      console.error("Error fetching target currency:", toError);
      return null;
    }
    
    const fromCode = fromCurrency.code.toLowerCase();
    const toCode = toCurrency.code.toLowerCase();
    
    try {
      let rate = null;
      
      switch (this.currentProvider) {
        case "exchange-api":
          rate = await this.fetchRateFromExchangeApi(fromCode, toCode);
          break;
        case "openexchangerates":
          rate = await this.fetchRateFromOpenExchangeRates(fromCode, toCode);
          break;
        case "exchangerate-api":
          rate = await this.fetchRateFromExchangeRateApi(fromCode, toCode);
          break;
        case "exchangeratesapi":
          rate = await this.fetchRateFromExchangeRatesApi(fromCode, toCode);
          break;
        case "freecurrencyapi":
          rate = await this.fetchRateFromFreeCurrencyApi(fromCode, toCode);
          break;
        default:
          console.error(`Unknown exchange rate provider: ${this.currentProvider}`);
          return null;
      }
      
      if (rate) {
        // Store the fetched rate in the database for future use
        const { error: insertError } = await supabase
          .from('exchange_rates')
          .upsert({
            base_currency_id: fromCurrencyId,
            target_currency_id: toCurrencyId,
            rate: rate,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'base_currency_id,target_currency_id'
          });
          
        if (insertError) {
          console.error("Error storing exchange rate:", insertError);
        } else {
          console.log(`Stored exchange rate: ${fromCode}/${toCode} = ${rate}`);
          
          // Cache the result
          this.exchangeRatesCache.set(`${fromCurrencyId}-${toCurrencyId}`, {
            id: `${fromCurrencyId}-${toCurrencyId}`,
            baseCurrencyId: fromCurrencyId,
            targetCurrencyId: toCurrencyId,
            rate: rate,
            updatedAt: new Date()
          });
        }
        
        return rate;
      } else {
        console.error("Missing rate in API response");
        return null;
      }
    } catch (error) {
      console.error(`Error fetching exchange rate from ${this.currentProvider}:`, error);
      return null;
    }
  }
  
  private async fetchRateFromExchangeApi(fromCode: string, toCode: string): Promise<number | null> {
    const url = `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/${fromCode}.json`;
    
    console.log(`Fetching exchange rate from ${url}`);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    
    // The API returns data in format { date: "2023-04-10", nok: { usd: 0.0956, ... } }
    if (!data[fromCode] || !data[fromCode][toCode]) {
      console.error(`No exchange rate found for ${fromCode} to ${toCode}`);
      return null;
    }
    
    return data[fromCode][toCode];
  }
  
  private async fetchRateFromOpenExchangeRates(fromCode: string, toCode: string): Promise<number | null> {
    const apiKey = this.apiKeys.openexchangerates;
    if (!apiKey) {
      console.error("OpenExchangeRates API key not set");
      return null;
    }
    
    const url = `https://openexchangerates.org/api/latest.json?app_id=${apiKey}&base=USD`;
    
    console.log(`Fetching exchange rate from OpenExchangeRates`);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`OpenExchangeRates API request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    
    // OpenExchangeRates free plan only allows USD as base, so we need to convert
    if (fromCode === 'usd') {
      // Direct conversion from USD to target
      return data.rates[toCode.toUpperCase()];
    } else {
      // Convert from USD to fromCode, then to toCode
      const usdToFrom = data.rates[fromCode.toUpperCase()];
      const usdToTo = data.rates[toCode.toUpperCase()];
      
      if (!usdToFrom || !usdToTo) {
        console.error(`Could not find rates for ${fromCode.toUpperCase()} or ${toCode.toUpperCase()}`);
        return null;
      }
      
      // Convert: fromCurrency -> USD -> toCurrency
      return usdToTo / usdToFrom;
    }
  }
  
  private async fetchRateFromExchangeRateApi(fromCode: string, toCode: string): Promise<number | null> {
    const apiKey = this.apiKeys['exchangerate-api'];
    if (!apiKey) {
      console.error("ExchangeRate-API key not set");
      return null;
    }
    
    const url = `https://v6.exchangerate-api.com/v6/${apiKey}/latest/${fromCode.toUpperCase()}`;
    
    console.log(`Fetching exchange rate from ExchangeRate-API`);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`ExchangeRate-API request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.result !== 'success') {
      console.error("ExchangeRate-API returned an error:", data.error);
      return null;
    }
    
    return data.conversion_rates[toCode.toUpperCase()];
  }
  
  private async fetchRateFromExchangeRatesApi(fromCode: string, toCode: string): Promise<number | null> {
    const apiKey = this.apiKeys.exchangeratesapi;
    if (!apiKey) {
      console.error("ExchangeRatesAPI key not set");
      return null;
    }
    
    const url = `https://api.exchangeratesapi.io/v1/latest?access_key=${apiKey}&base=${fromCode.toUpperCase()}&symbols=${toCode.toUpperCase()}`;
    
    console.log(`Fetching exchange rate from ExchangeRatesAPI`);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`ExchangeRatesAPI request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      console.error("ExchangeRatesAPI returned an error:", data.error);
      return null;
    }
    
    return data.rates[toCode.toUpperCase()];
  }
  
  private async fetchRateFromFreeCurrencyApi(fromCode: string, toCode: string): Promise<number | null> {
    const apiKey = this.apiKeys.freecurrencyapi;
    if (!apiKey) {
      console.error("FreeCurrencyAPI key not set");
      return null;
    }
    
    const url = `https://api.freecurrencyapi.com/v1/latest?apikey=${apiKey}&base_currency=${fromCode.toUpperCase()}&currencies=${toCode.toUpperCase()}`;
    
    console.log(`Fetching exchange rate from FreeCurrencyAPI`);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`FreeCurrencyAPI request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.data) {
      console.error("FreeCurrencyAPI returned an error:", data);
      return null;
    }
    
    return data.data[toCode.toUpperCase()];
  }
  
  public async fetchAndStoreExchangeRates(baseCurrency: Currency, targetCurrencies: Currency[]): Promise<void> {
    const baseCurrencyCode = baseCurrency.code.toLowerCase();
    console.log(`Fetching rates for ${baseCurrencyCode} against ${targetCurrencies.map(c => c.code).join(', ')}`);
    
    const updatedAt = new Date().toISOString();
    const upsertData = [];
    
    try {
      let ratesData: Record<string, number> = {};
      
      // Fetch rates using the selected provider
      switch (this.currentProvider) {
        case "exchange-api":
          ratesData = await this.fetchAllRatesFromExchangeApi(baseCurrencyCode);
          break;
        case "openexchangerates":
          ratesData = await this.fetchAllRatesFromOpenExchangeRates(baseCurrencyCode);
          break;
        case "exchangerate-api":
          ratesData = await this.fetchAllRatesFromExchangeRateApi(baseCurrencyCode);
          break;
        case "exchangeratesapi":
          ratesData = await this.fetchAllRatesFromExchangeRatesApi(baseCurrencyCode);
          break;
        case "freecurrencyapi":
          ratesData = await this.fetchAllRatesFromFreeCurrencyApi(baseCurrencyCode);
          break;
        default:
          throw new Error(`Unknown exchange rate provider: ${this.currentProvider}`);
      }
      
      if (Object.keys(ratesData).length === 0) {
        throw new Error(`No rates found for ${baseCurrencyCode}`);
      }
      
      // Process each target currency
      for (const targetCurrency of targetCurrencies) {
        // Skip if it's the same currency
        if (targetCurrency.id === baseCurrency.id) continue;
        
        const targetCode = targetCurrency.code.toLowerCase();
        const rate = ratesData[targetCode];
        
        if (rate) {
          upsertData.push({
            base_currency_id: baseCurrency.id,
            target_currency_id: targetCurrency.id,
            rate: rate,
            updated_at: updatedAt
          });
          
          // Update the cache
          this.exchangeRatesCache.set(`${baseCurrency.id}-${targetCurrency.id}`, {
            id: `${baseCurrency.id}-${targetCurrency.id}`,
            baseCurrencyId: baseCurrency.id,
            targetCurrencyId: targetCurrency.id,
            rate: rate,
            updatedAt: new Date(updatedAt)
          });
        } else {
          console.warn(`No rate found for ${baseCurrencyCode} to ${targetCode}`);
        }
      }
    } catch (error) {
      console.error(`Error fetching rates for ${baseCurrencyCode}:`, error);
      throw error;
    }
    
    // Batch update the database
    if (upsertData.length > 0) {
      const { error } = await supabase
        .from('exchange_rates')
        .upsert(upsertData, {
          onConflict: 'base_currency_id,target_currency_id'
        });
        
      if (error) {
        console.error("Error batch storing exchange rates:", error);
      } else {
        console.log(`Successfully updated ${upsertData.length} exchange rates for ${baseCurrencyCode}`);
      }
    }
  }
  
  private async fetchAllRatesFromExchangeApi(baseCurrencyCode: string): Promise<Record<string, number>> {
    const url = `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/${baseCurrencyCode}.json`;
    
    console.log(`Fetching all exchange rates from ${url}`);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data[baseCurrencyCode]) {
      throw new Error(`No rates found for ${baseCurrencyCode}`);
    }
    
    return data[baseCurrencyCode];
  }
  
  private async fetchAllRatesFromOpenExchangeRates(baseCurrencyCode: string): Promise<Record<string, number>> {
    const apiKey = this.apiKeys.openexchangerates;
    if (!apiKey) {
      throw new Error("OpenExchangeRates API key not set");
    }
    
    // OpenExchangeRates free plan only allows USD as base
    const url = `https://openexchangerates.org/api/latest.json?app_id=${apiKey}`;
    
    console.log(`Fetching all exchange rates from OpenExchangeRates`);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`OpenExchangeRates API request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    
    if (baseCurrencyCode.toLowerCase() === 'usd') {
      // Direct conversion from USD
      return Object.entries(data.rates).reduce((acc, [currency, rate]) => {
        acc[currency.toLowerCase()] = rate as number;
        return acc;
      }, {} as Record<string, number>);
    } else {
      // Need to convert from USD to base currency, then to all others
      const usdToBase = data.rates[baseCurrencyCode.toUpperCase()];
      
      if (!usdToBase) {
        throw new Error(`Could not find rate for USD to ${baseCurrencyCode.toUpperCase()}`);
      }
      
      return Object.entries(data.rates).reduce((acc, [currency, rate]) => {
        // Convert: USD -> baseCurrency -> targetCurrency
        acc[currency.toLowerCase()] = (rate as number) / usdToBase;
        return acc;
      }, {} as Record<string, number>);
    }
  }
  
  private async fetchAllRatesFromExchangeRateApi(baseCurrencyCode: string): Promise<Record<string, number>> {
    const apiKey = this.apiKeys['exchangerate-api'];
    if (!apiKey) {
      throw new Error("ExchangeRate-API key not set");
    }
    
    const url = `https://v6.exchangerate-api.com/v6/${apiKey}/latest/${baseCurrencyCode.toUpperCase()}`;
    
    console.log(`Fetching all exchange rates from ExchangeRate-API`);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`ExchangeRate-API request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.result !== 'success') {
      throw new Error(`ExchangeRate-API returned an error: ${data.error}`);
    }
    
    return Object.entries(data.conversion_rates).reduce((acc, [currency, rate]) => {
      acc[currency.toLowerCase()] = rate as number;
      return acc;
    }, {} as Record<string, number>);
  }
  
  private async fetchAllRatesFromExchangeRatesApi(baseCurrencyCode: string): Promise<Record<string, number>> {
    const apiKey = this.apiKeys.exchangeratesapi;
    if (!apiKey) {
      throw new Error("ExchangeRatesAPI key not set");
    }
    
    const url = `https://api.exchangeratesapi.io/v1/latest?access_key=${apiKey}&base=${baseCurrencyCode.toUpperCase()}`;
    
    console.log(`Fetching all exchange rates from ExchangeRatesAPI`);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`ExchangeRatesAPI request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(`ExchangeRatesAPI returned an error: ${data.error}`);
    }
    
    return Object.entries(data.rates).reduce((acc, [currency, rate]) => {
      acc[currency.toLowerCase()] = rate as number;
      return acc;
    }, {} as Record<string, number>);
  }
  
  private async fetchAllRatesFromFreeCurrencyApi(baseCurrencyCode: string): Promise<Record<string, number>> {
    const apiKey = this.apiKeys.freecurrencyapi;
    if (!apiKey) {
      throw new Error("FreeCurrencyAPI key not set");
    }
    
    const url = `https://api.freecurrencyapi.com/v1/latest?apikey=${apiKey}&base_currency=${baseCurrencyCode.toUpperCase()}`;
    
    console.log(`Fetching all exchange rates from FreeCurrencyAPI`);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`FreeCurrencyAPI request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.data) {
      throw new Error(`FreeCurrencyAPI returned an error: ${JSON.stringify(data)}`);
    }
    
    return Object.entries(data.data).reduce((acc, [currency, rate]) => {
      acc[currency.toLowerCase()] = rate as number;
      return acc;
    }, {} as Record<string, number>);
  }
}

// Export a singleton instance
export const currencyConverter = CurrencyConverter.getInstance();
